import { useEffect } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { type Dispatch, type UnknownAction } from "redux";
import { format, parseISO } from "date-fns";
import { Calendar, Globe, User, Users } from "lucide-react";

import { WFPButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";

import MarkdownPreview from "../../../../components/MarkdownPreview";
import {
  infoHubSubjectDetail,
  infoHubSubjectDetailReset,
  listAggregators,
  listCountries,
  listInfoHubSubjectAggregators,
  listInfoHubSubjectAggregatorsReset,
} from "../../../../actions";
import { getCountryObjectFromCache } from "../../../../utils";
import type { EntityId, InfoHubSubjectAggregator, InfoHubSubjectDetail } from "../../types";
import type { CountryOption } from "../SubjectsDataTable/columns";

type ValidateType = "publish" | "withdraw" | "resume" | "copy";

interface ViewModalOptions {
  isOpen: boolean;
  id?: EntityId | null;
}

interface ViewModalActions {
  publish: (id: EntityId) => void;
  withdraw: (id: EntityId) => void;
}

interface OwnProps {
  modalOptions: ViewModalOptions;
  setModalOptions: (options: { isOpen: boolean; id?: EntityId | null }) => void;
  actions: ViewModalActions;
}

interface StateProps {
  infoHubSubject?: InfoHubSubjectDetail;
  infoHubSubjectAggregators: Array<InfoHubSubjectAggregator | undefined>;
  isFetching: boolean;
  countries: CountryOption[];
  country: CountryOption;
}

interface DispatchProps {
  infoHubSubjectDetail: (id: EntityId) => void;
  infoHubSubjectDetailReset: () => void;
  listInfoHubSubjectAggregators: (id: EntityId) => void;
  listInfoHubSubjectAggregatorsReset: () => void;
  listAggregators: () => void;
  listCountries: () => void;
}

type ViewModalProps = OwnProps & StateProps & DispatchProps;

export const ViewModal = ({
  modalOptions,
  setModalOptions,
  actions,
  infoHubSubject,
  infoHubSubjectAggregators,
  isFetching,
  countries,
  country,
  infoHubSubjectDetail,
  infoHubSubjectDetailReset,
  listInfoHubSubjectAggregators,
  listInfoHubSubjectAggregatorsReset,
  listAggregators,
  listCountries,
}: ViewModalProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id, isOpen } = modalOptions;

  useEffect(() => {
    if (!id) return;
    infoHubSubjectDetail(id);
    listCountries();
    listInfoHubSubjectAggregators(id);
    listAggregators();
  }, [id, infoHubSubjectDetail, listCountries, listInfoHubSubjectAggregators, listAggregators]);

  const onClose = () => {
    infoHubSubjectDetailReset();
    listInfoHubSubjectAggregatorsReset();
    setModalOptions({ isOpen: false, id: null });
  };

  const subjectCountry = infoHubSubject
    ? countries.find(c => c.id === infoHubSubject.country)
    : undefined;
  const isSubjectWithinUserCountry = !!subjectCountry && subjectCountry.id === country.id;

  const getValidateType = (): ValidateType => {
    if (infoHubSubject?.status === "draft") return "publish";
    if (isSubjectWithinUserCountry && infoHubSubject && infoHubSubject.status !== "withdrawn")
      return "withdraw";
    if (isSubjectWithinUserCountry && infoHubSubject?.status === "withdrawn") return "resume";
    return "copy";
  };
  const validateType = getValidateType();

  const onValidate = () => {
    if (!id) return;
    switch (validateType) {
      case "withdraw":
        actions.withdraw(id);
        onClose();
        break;
      case "resume":
        history.push(`/wfpManager/infohub/newContent/${id}`);
        break;
      case "copy":
        history.push(`/wfpManager/infohub/newContent/${id}`, { isCopy: true });
        break;
      case "publish":
        actions.publish(id);
        onClose();
        break;
    }
  };

  const validateLabel: Record<ValidateType, string> = {
    withdraw: t("infoHub.table.actions.withdraw"),
    resume: t("infoHub.table.actions.resume"),
    copy: t("infoHub.makeCopy"),
    publish: t("infoHub.table.actions.publish"),
  };
  const validateVariant = validateType === "withdraw" ? "destructive" : "default";

  const publishedBy = infoHubSubject?.creation_user
    ? `${infoHubSubject.creation_user.first_name ?? ""} ${infoHubSubject.creation_user.last_name ?? ""}`.trim()
    : "Unknown";
  const countryName = subjectCountry?.name ?? "Unknown";
  const createdAt = infoHubSubject?.creation_datetime
    ? format(parseISO(infoHubSubject.creation_datetime), "d MMM yyyy, HH:mm")
    : "";

  return (
    <Dialog
      open={isOpen}
      onOpenChange={next => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl" data-testid="view-modal">
        {isFetching || !infoHubSubject ? (
          <>
            <DialogTitle className="sr-only">{t("infoHub.longTitle")}</DialogTitle>
            <div className="flex min-h-40 items-center justify-center">
              <Spinner className="size-6 text-muted-foreground" />
            </div>
          </>
        ) : (
          <>
            <DialogHeader className="gap-3 pr-8 text-left">
              <p className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
                {infoHubSubject.content_type}
              </p>
              <DialogTitle className="text-2xl">{infoHubSubject.name}</DialogTitle>
              {infoHubSubject.topics && infoHubSubject.topics.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {infoHubSubject.topics.map(topic => (
                    <Badge key={topic.id} variant="secondary">
                      {topic.name}
                    </Badge>
                  ))}
                </div>
              )}
              <DialogDescription asChild>
                <div className="flex flex-col gap-1.5 text-sm sm:flex-row sm:flex-wrap sm:gap-x-6">
                  <span className="flex items-center gap-2">
                    <Globe className="size-4 shrink-0" aria-hidden="true" />
                    <span className="font-medium">{t("infoHub.country")}:</span> {countryName}
                  </span>
                  <span className="flex items-center gap-2">
                    <User className="size-4 shrink-0" aria-hidden="true" />
                    <span className="font-medium">{t("infoHub.publishedBy")}:</span> {publishedBy}
                  </span>
                  <span className="flex items-center gap-2">
                    <Calendar className="size-4 shrink-0" aria-hidden="true" />
                    <span className="font-medium">{t("infoHub.date")}:</span> {createdAt}
                  </span>
                </div>
              </DialogDescription>
            </DialogHeader>

            <Separator />

            <MarkdownPreview source={infoHubSubject.content} />

            {isSubjectWithinUserCountry && infoHubSubjectAggregators.length > 0 && (
              <section className="space-y-2">
                <h3 className="flex items-center gap-2 font-semibold text-foreground text-sm">
                  <Users className="size-4 shrink-0" aria-hidden="true" />
                  {t("infoHub.form.sharedWith")}
                </h3>
                <ul className="space-y-1 text-muted-foreground text-sm">
                  {infoHubSubjectAggregators.map((aggregator, index) => (
                    <li key={aggregator?.id ?? `aggregator-${index}`}>
                      {aggregator?.name ??
                        t("infoHub.form.unknownAggregator", { id: aggregator?.id })}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <DialogFooter>
              <WFPButton type="button" variant="outline" onClick={onClose}>
                {t("infoHub.close")}
              </WFPButton>
              <WFPButton type="button" variant={validateVariant} onClick={onValidate}>
                {validateLabel[validateType]}
              </WFPButton>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

interface ViewModalState {
  infoHubSubjectDetail: {
    data: InfoHubSubjectDetail | Record<string, never>;
    isFetching: boolean;
  };
  listInfoHubSubjectAggregators: { data: EntityId[] | null; isFetching: boolean };
  listAggregators: { data: { results?: InfoHubSubjectAggregator[] }; isFetching: boolean };
  listCountries: { data: { results?: CountryOption[] }; isFetching: boolean };
}

export const mapStateToProps = (state: ViewModalState): StateProps => {
  const allAggregators = state.listAggregators.data.results ?? [];
  const selected = state.listInfoHubSubjectAggregators.data;
  const subjectData = state.infoHubSubjectDetail.data;

  return {
    infoHubSubject:
      subjectData && Object.keys(subjectData).length > 0
        ? (subjectData as InfoHubSubjectDetail)
        : undefined,
    infoHubSubjectAggregators: Array.isArray(selected)
      ? selected.map(aggregatorId => allAggregators.find(a => a.id === aggregatorId))
      : [],
    isFetching:
      state.infoHubSubjectDetail.isFetching ||
      state.listCountries.isFetching ||
      state.listAggregators.isFetching ||
      state.listInfoHubSubjectAggregators.isFetching,
    countries: state.listCountries.data.results ?? [],
    country: getCountryObjectFromCache(),
  };
};

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => ({
  infoHubSubjectDetail: id => dispatchAction(dispatch, infoHubSubjectDetail(String(id))),
  infoHubSubjectDetailReset: () => dispatchAction(dispatch, infoHubSubjectDetailReset()),
  listInfoHubSubjectAggregators: id =>
    dispatchAction(dispatch, listInfoHubSubjectAggregators({ subject: id }, "aggregators/")),
  listInfoHubSubjectAggregatorsReset: () =>
    dispatchAction(dispatch, listInfoHubSubjectAggregatorsReset()),
  listAggregators: () => dispatchAction(dispatch, listAggregators({ page_size: 9999 })),
  listCountries: () => dispatchAction(dispatch, listCountries()),
});

export const ConnectedViewModal = connect(mapStateToProps, mapDispatchToProps)(ViewModal);
